import { useEffect, useState, useCallback } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SponsorsPage() {
  usePageTitle("Sponsoren");
  const [sponsors, setSponsors] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sponsors`).then(r => setSponsors(r.data)).catch(() => {});
  }, []);

  const next = useCallback(() => {
    if (sponsors.length === 0) return;
    setCurrent(c => (c + 1) % sponsors.length);
  }, [sponsors.length]);

  useEffect(() => {
    if (sponsors.length <= 1 || paused) return;
    const timer = setInterval(next, 3000);
    return () => clearInterval(timer);
  }, [sponsors.length, paused, next]);

  const getIndex = (offset) => {
    if (sponsors.length === 0) return 0;
    return ((current + offset) % sponsors.length + sponsors.length) % sponsors.length;
  };

  const getSrc = (s) => s.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}` : s.logo_url;

  if (sponsors.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="sponsors-page">
        <p className="text-sm text-muted-foreground">Aktuell keine Sponsoren vorhanden.</p>
      </div>
    );
  }

  const prev = sponsors[getIndex(-1)];
  const center = sponsors[getIndex(0)];
  const nxt = sponsors[getIndex(1)];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center overflow-hidden" data-testid="sponsors-page"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative w-full flex items-center justify-center" style={{ height: '100vh' }}>

        {/* Previous logo — left ~20% visible */}
        <div className="absolute transition-all duration-700 ease-in-out cursor-pointer"
          style={{ left: '-20%', width: '40%' }}
          onClick={() => setCurrent(getIndex(-1))}
          data-testid="carousel-prev">
          <div className="bg-white rounded-lg p-8 mx-auto" style={{ maxWidth: '80%' }}>
            <img src={getSrc(prev)} alt={prev.name}
              className="w-full h-auto object-contain mx-auto transition-all duration-700"
              style={{ maxHeight: '30vh', opacity: 0.3 }} />
          </div>
        </div>

        {/* Center logo — ~50% screen width */}
        <a href={center.link || "#"} target={center.link ? "_blank" : "_self"} rel="noopener noreferrer"
          className="relative z-10 transition-all duration-700 ease-in-out block"
          style={{ width: '50%' }}
          data-testid="carousel-center">
          <div className="bg-white rounded-lg p-10 shadow-2xl shadow-white/5">
            <img src={getSrc(center)} alt={center.name}
              className="w-full h-auto object-contain mx-auto transition-all duration-700"
              style={{ maxHeight: '50vh' }} />
          </div>
        </a>

        {/* Thank you text - fixed at top */}
        <p className="absolute top-32 left-1/2 -translate-x-1/2 text-center font-bold tracking-wider z-20"
          style={{ fontFamily: 'Oswald', fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          data-testid="sponsor-thank-you">
          <span className="text-white">Danke für die </span>
          <span style={{ color: '#fbbf24' }}>Unterstützung!</span>
        </p>

        {/* Next logo — right ~20% visible */}
        <div className="absolute transition-all duration-700 ease-in-out cursor-pointer"
          style={{ right: '-20%', width: '40%' }}
          onClick={() => setCurrent(getIndex(1))}
          data-testid="carousel-next">
          <div className="bg-white rounded-lg p-8 mx-auto" style={{ maxWidth: '80%' }}>
            <img src={getSrc(nxt)} alt={nxt.name}
              className="w-full h-auto object-contain mx-auto transition-all duration-700"
              style={{ maxHeight: '30vh', opacity: 0.3 }} />
          </div>
        </div>

        {/* Dots */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-2.5 z-20">
          {sponsors.map((s, i) => (
            <button key={s.id || `dot-${i}`} onClick={() => setCurrent(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'bg-[#fbbf24] w-8' : 'bg-white/25 w-2 hover:bg-white/50'}`}
              data-testid={`carousel-dot-${i}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
